import React, { useState } from "react";
import { Button, Container, Row, Col, Progress, Modal, ModalBody } from "reactstrap";
import { RiUserAddLine } from "react-icons/ri";

function InstructionsWizard({ stepsData }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    const totalSteps = stepsData.length;
    const step = stepsData[currentStep];

    // Allow step control from inline HTML links
    React.useEffect(() => {
        window.__goToStep = (stepIndex) => {
            if (stepIndex >= 0 && stepIndex < totalSteps) {
                setCurrentStep(stepIndex);
            }
        };

        return () => {
            delete window.__goToStep;
        };
    }, [totalSteps]);

    const handleNext = () => {
        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const uiUrl = window.env.UI_BASE_URL || "http://localhost:3000"; // Fallback to localhost

    // Safely determine the image source
    const imageSrc = step.image
        ? step.image.startsWith("http")
            ? step.image
            : uiUrl + step.image
        : null;

    // Determine if the image is enlargeable (default to true)
    const isEnlargeable = step.enlargeable === undefined ? true : step.enlargeable;

    const toggleModal = () => setModalOpen(!modalOpen);

    return (
        <Container className="">
            <Row className="justify-content-center">
                <Col lg="4" className="text-center">
                    {/* Step List: allow direct navigation to a step */}
                    <div className="mb-4">
                        <ul className="list-group">
                            {stepsData.map((stepItem, index) => (
                                <li key={index} className="list-group-item">
                                    <Button
                                        color={currentStep === index ? "primary" : "link"}
                                        onClick={() => setCurrentStep(index)}
                                    >
                                        {index + 1}. {stepItem.title}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Col>
                <Col lg="8" className="text-center">
                    {/* Display the current step details */}
                    <div className="d-flex align-items-center mb-3">
                        <h2 style={{fontSize: "1rem"}} className="text-primary mb-0">
                            {step.title}
                        </h2>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                        <Button
                            color="secondary"
                            disabled={currentStep === 0}
                            onClick={handlePrev}
                            style={{height: "40px"}}
                        >
                            Previous
                        </Button>
                        <Button
                            color="primary"
                            disabled={currentStep === totalSteps - 1}
                            onClick={handleNext}
                            style={{height: "40px"}}
                        >
                            Next
                        </Button>
                    </div>

                    {/* Conditionally render image with or without enlarge functionality */}
                    {imageSrc && (
                        <div className="my-3">
                            <img
                                src={imageSrc}
                                alt={`Step ${currentStep + 1}`}
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                    cursor: isEnlargeable ? "pointer" : "default"
                                }}
                                onClick={isEnlargeable ? toggleModal : undefined}
                            />
                            {isEnlargeable && (
                                <small className="text-muted d-block mt-2">
                                    Click the image to enlarge it.
                                </small>
                            )}
                        </div>
                    )}

                    <p className="lead" dangerouslySetInnerHTML={{__html: step.description}}/>

                    {/* Optional progress bar */}
                    <Progress animated value={((currentStep + 1) / totalSteps) * 100} className="mb-4"/>

                    <div className="d-flex justify-content-between mt-4">
                        <div>
                            <Button
                                color="secondary"
                                disabled={currentStep === 0}
                                onClick={handlePrev}
                                style={{height: "40px"}}
                            >
                                Previous
                            </Button>
                        </div>
                        <div>
                            <h4>
                                Step {currentStep + 1} of {totalSteps}
                            </h4>
                        </div>
                        <div>
                            <Button
                                color="primary"
                                disabled={currentStep === totalSteps - 1}
                                onClick={handleNext}
                                style={{height: "40px"}}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Modal for enlarged image */}
            {isEnlargeable && (
                <Modal isOpen={modalOpen} toggle={toggleModal} centered size="lg">
                    <ModalBody className="p-0">
                        {imageSrc && (
                            <img
                                src={imageSrc}
                                alt={`Step ${currentStep + 1}`}
                                style={{width: "100%", height: "auto"}}
                            />
                        )}
                        <div className="text-center my-3">
                            <Button color="secondary" onClick={toggleModal}>
                                Close
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>
            )}
        </Container>
    );
}

export default InstructionsWizard;
